/**
 * @file BoardroomSetupPage.tsx
 * @brief Web boardroom setup — mobile MeetingSetup parity (title, languages,
 *        company roster + share, external clients + email lookup, mic).
 *
 * 30-09-2026: mobile's bare picker rows and copy.
 * 01-10-2026: Title (full row), then Participants + Languages side by
 * side, then Agenda (full row), per DES.
 *
 * 30-09-2026: one Participants picker, as on mobile. Its search filters
 * company users AND finds or adds external people (ExternalFromSearch);
 * the separate "External Clients" card is gone.
 *
 * 06-10-2026 (ADR-030): a Mode choice, Meeting notes or Full intelligence,
 * passed to the recorder as `tier`. A route state or `?tier=` query (the
 * Notes page's "Change") picks the start value. Scheduling for later stays a
 * full meeting.
 *
 * 07-10-2026 (DES, the phone's daf9716, 0e90939, 842f65d, 7f996d8): Mode is
 * the first section and defaults to Full intelligence (Meeting Notes starts
 * from the Notes page in one slide). Meeting notes shows only what Notes
 * uses: no Participants or Agenda, and one "Notes Language" (single select)
 * that a Notes start sends alone and remembers for the Notes page.
 *
 * 12-10-2026 (DES, agendas): the Agenda picker lists agendas only and has
 * "Write new agenda" at the top. That opens the editor in agenda mode; this
 * page first keeps its form in its own history entry (`setupDraft`), and on
 * the way back takes the saved agenda (agendaHandoff.ts) and attaches it.
 *
 * 20-10-2026 (DES 19-10-2026, pricing in the flow, web + Mac only): Full
 * intelligence that the user's allowance does not cover says the rule in one
 * sentence under Mode before the start (recorderPriceHints.ts, ADR-045).
 *
 * @author DES
 * @date 28-07-2026
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMySubscription } from "@/features/my-billing/api";
import { fullIntelligenceNotice } from "./recorderPriceHints";
import { FreeMinutesNotice } from "@/features/my-billing/components/FreeMinutesNotice";
import { fairUseStartBlocked, freeStartBlocked } from "@/features/my-billing/freeMinutes";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  AudioLines,
  CalendarClock,
  ChevronDown,
  ListOrdered,
  Mic,
  NotebookPen,
  Plus,
  Sparkles,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { MeetingNotesPen } from "@/components/icons/MeetingNotesPen";
import { Avatar } from "@/components/ui/Avatar";
import { FormSheetTabs, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import {
  FormFieldGroup,
  FormSheetField,
  FormSheetFields,
  formGridLinesHiddenClass,
} from "@/components/ui/FormSheet";
import { NotePickerSheet, type PickedNote } from "@/features/notes/NotePickerSheet";
import { Input } from "@/components/ui/Input";
import { Switch } from "@/components/ui/Switch";
import { SwipeButton } from "@/components/ui/SwipeButton";
import { LanguagePicker } from "@/components/ui/LanguagePicker";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { apiGet } from "@/shared/api/client";
import { companyEndpoints } from "@/shared/api/endpoints";
import { useAuth } from "@/shared/auth/auth-context";
import { transparencyLine, useRecordingViewerFlags } from "./recordingViewer";
import { useDataControls } from "@/shared/auth/meExtras";
import type { CalendarMeetingPrefill } from "./calendarMeetings";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { getInitials } from "@/shared/utils/formatters";
import { useMicDevices } from "./capture/useMicDevices";
import {
  MAX_AGENDA_NOTES,
  type BoardroomDraftParticipant,
  type BoardroomMeetingTier,
} from "./capture/boardroomDraftStore";
import { probeDurableStorage } from "./capture/durabilityProbe";
import { describeRecorderSupportError } from "./capture/micErrors";
import { MicPicker, describeMicInUse } from "./MicPicker";
import { useVoiceEnrollment } from "@/features/voice-enrollment/api";
import { canCaptureSystemAudio } from "./capture/systemAudio";
import { useDesktopDefaultMic, useDesktopSettings } from "@/shared/desktop/useDesktopSettings";
import { ExternalFromSearch } from "./ExternalFromSearch";
import { FULL_EMAIL_RE, hasExternalBlock, useGuestLookup } from "./participantSearch";
import { ScheduleBoardroomDialog } from "./ScheduleBoardroomDialog";
import { isStandInEmail, scheduleAgenda } from "./scheduledSide";
import { useBoardroomBasePath } from "./useBoardroomBasePath";
import { loadNotesLanguage, saveNotesLanguage } from "@/features/notes/notesLanguage";
import { takeNewAgenda, type AgendaPickerState } from "@/features/notes/agendaHandoff";

const STACK_OVERLAP = 18;
/** Avatars shown in the stack before a "+N" chip. */
const STACK_MAX = 5;

/* Picker row inside a bare form field (01-10-2026, the Interpreter's form
   style): 20px icon, 17px medium value, chevron, the bare control's 24px
   height so every field's hairline lines up. */
const fieldRowClass =
  "flex min-h-6 w-full items-center gap-2 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";
const fieldRowIconClass = "h-5 w-5 shrink-0 text-muted-foreground";
const fieldRowTextClass =
  "min-w-0 flex-1 truncate text-[17px] font-medium leading-6";
const fieldRowChevronClass = "h-5 w-5 shrink-0 text-muted-foreground";

type CompanyUserRow = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  status?: string;
  profile_image_url?: string | null;
};

/** A row of the picker: a colleague, or an external person already added. */
type RosterPickerItem = PickerSheetItem &
  (
    | { user: CompanyUserRow; external?: undefined }
    | { user?: undefined; external: BoardroomDraftParticipant }
  );

function isFullEmail(v: string): boolean {
  return FULL_EMAIL_RE.test(v.trim());
}

/** Older builds gave guests without an email a stand-in address; never
 *  show it (B48: new guests always have a real one). */
function isPlaceholderEmail(email: string): boolean {
  return isStandInEmail(email);
}

// ----------------------------------------------------------------------
// Mode: Meeting notes or Full intelligence (ADR-030, DES 06-10-2026)
// The phone's MeetingSetup Mode section, same labels, icons and copy.
// Related: FRONTEND/src/screens/MeetingIntelligence/MeetingSetup.tsx
// ----------------------------------------------------------------------
const MODE_TABS: FormSheetTab<BoardroomMeetingTier>[] = [
  { id: "notes", label: "Meeting notes", icon: MeetingNotesPen }, // DES 08-10-2026: app icon, was NotebookPen
  { id: "full", label: "Full intelligence", icon: Sparkles },
];

const MODE_HINT: Record<BoardroomMeetingTier, string> = {
  notes:
    // Sharing v2 (DES 21-10-2026): shared like a Boardroom, so no "only you".
    "Transcript, your notes enhanced, a summary and to-dos. Shared with the participants you pick, and you can upgrade it to full intelligence later.",
  full: "Named speakers, decisions checked against the transcript, agenda check, translation and PDF minutes. Shared with the participants.",
};

/**
 * The form as it was left for "Write new agenda" (DES 12-10-2026): kept in
 * this page's history entry, so going back finds the meeting unchanged.
 */
type SetupDraft = {
  tier: BoardroomMeetingTier;
  title: string;
  prepNotes: PickedNote[];
  targetLanguages: string[];
  selected: BoardroomDraftParticipant[];
  deviceId: string;
  /** Only when changed here; null follows Settings. */
  includeCallAudio: boolean | null;
};

function displayName(u: CompanyUserRow): string {
  return [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email;
}

export function BoardroomSetupPage() {
  const navigate = useNavigate();
  const base = useBoardroomBasePath();
  const { user } = useAuth();
  // A meeting from the Mac's calendar (CalendarMeetingsPanel, 06-10-2026):
  // its title and attendees pre-fill this new boardroom. Read once.
  const location = useLocation();
  const [fromCalendar] = useState<CalendarMeetingPrefill | null>(
    () => (location.state as { fromCalendar?: CalendarMeetingPrefill } | null)?.fromCalendar ?? null,
  );
  // Back from "Write new agenda" (DES 12-10-2026): the form as it was left.
  const [restored] = useState<SetupDraft | null>(
    () => (location.state as { setupDraft?: SetupDraft } | null)?.setupDraft ?? null,
  );
  // Full unless the caller asked for Notes (route state or `?tier=`): every
  // Boardroom entry means a full meeting; only the Notes page's "Change"
  // opens Setup in Notes mode (DES 07-10-2026, the phone's 842f65d).
  const [searchParams] = useSearchParams();
  const [tier, setTier] = useState<BoardroomMeetingTier>(() => {
    if (restored) return restored.tier;
    const asked =
      (location.state as { tier?: string } | null)?.tier ?? searchParams.get("tier");
    return asked === "notes" ? "notes" : "full";
  });
  const isNotes = tier === "notes";
  const viewerFlags = useRecordingViewerFlags(user?.id);
  const adminsLine = transparencyLine(viewerFlags.data, tier);
  // ADR-051: inside-only sharing. A registered person outside the company
  // keeps their place (named as a speaker) but gets no share; the server
  // drops it anyway, so the switch says so instead of pretending.
  const { canShareOutside, shareLine } = useDataControls();
  const canShareWith = (p: { isExternal?: boolean; isRegisteredUser?: boolean }) =>
    !p.isExternal || (Boolean(p.isRegisteredUser) && canShareOutside);
  // Full intelligence past the allowance: the rule in one sentence (ADR-045).
  const subscription = useMySubscription();
  const intelligenceNotice = fullIntelligenceNotice(subscription.data);
  // Free plan (B4, 22-10-2026): Meeting Notes can't start when the free
  // minutes are used or the email isn't confirmed yet; the notice says why.
  // Pro / Business (B6): the same at the month's fair use. Full stays open.
  // B43 (31-10-2026): Free Full Intelligence uses the same free minutes, so
  // it waits too (no pay as you go on Free); the name stays for its callers.
  const notesStartBlocked = isNotes
    ? freeStartBlocked(subscription.data?.freeMinutes, "notes") || fairUseStartBlocked(subscription.data?.fairUse)
    : freeStartBlocked(subscription.data?.freeMinutes, "full");
  const [title, setTitle] = useState(() => restored?.title ?? fromCalendar?.title ?? "");
  // Optional agenda (ADR-023). Chosen here because this is where the meeting
  // is being set up; the LINK is made after the recording creates the meeting,
  // since there is nothing to attach to yet. The title is held alongside the id
  // so the field can name the choice without a second lookup.
  // Several since 30-09-2026 (mobile parity, up to MAX_AGENDA_NOTES).
  const [prepNotes, setPrepNotes] = useState<PickedNote[]>(() => restored?.prepNotes ?? []);
  const [prepPickerOpen, setPrepPickerOpen] = useState(false);
  // An agenda written from the picker's "Write new agenda" comes back
  // attached (DES 12-10-2026, the phone's AGENDA_CREATED): added to the
  // list unless it is already there or the list is full.
  useEffect(() => {
    const created = takeNewAgenda();
    if (!created) return;
    setPrepNotes((cur) =>
      cur.some((n) => n.id === created.id) || cur.length >= MAX_AGENDA_NOTES
        ? cur
        : [...cur, created],
    );
  }, []);
  // Opened for Notes: the notes language the Notes page shows, so Change
  // starts from it (07-10-2026).
  const [targetLanguages, setTargetLanguages] = useState<string[]>(
    () => restored?.targetLanguages ?? [tier === "notes" ? loadNotesLanguage() : "en-GB"],
  );
  // Meeting Notes writes in ONE language (the server uses the first): going
  // to Notes keeps only the first (DES 06-10-2026, the phone's 0e90939).
  const changeTier = (next: BoardroomMeetingTier) => {
    setTier(next);
    if (next === "notes") setTargetLanguages((prev) => (prev.length > 1 ? prev.slice(0, 1) : prev));
  };
  // Calendar attendees start as external guests by email; the effect below
  // swaps colleagues for their accounts once the roster loads.
  const [selected, setSelected] = useState<BoardroomDraftParticipant[]>(() =>
    restored?.selected ??
    (fromCalendar?.attendees ?? []).map((a) => ({
      id: `cal_${a.email.toLowerCase()}`,
      name: a.name,
      email: a.email,
      isExternal: true,
      shared: false,
    })),
  );
  const [deviceId, setDeviceId] = useState(() => restored?.deviceId ?? "");
  // Desktop app only. On by default: hearing the call is why the app exists,
  // and in a room meeting with no call running it records nothing extra.
  const desktopCallAudio = canCaptureSystemAudio();
  const [includeCallAudio, setIncludeCallAudio] = useState(
    () => restored?.includeCallAudio ?? true,
  );
  const [starting, setStarting] = useState(false);
  const [guestName, setGuestName] = useState("");
  // The picker's one search: filters colleagues, finds/adds external people.
  const [pickerSearch, setPickerSearch] = useState("");
  const [shareWithGuest, setShareWithGuest] = useState(false);
  const [participantsExpanded, setParticipantsExpanded] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const { devices, error: micError, loading: micsLoading, refresh } = useMicDevices({ hideVerblizrMic: true });
  const voice = useVoiceEnrollment();
  // Desktop app: the mic and call-audio choice from Settings (⌘,) start this
  // meeting's choices; changing them here is for this meeting only.
  useDesktopDefaultMic(devices, deviceId, setDeviceId);
  const desktopSettings = useDesktopSettings();
  const callAudioTouched = useRef(restored?.includeCallAudio != null);
  useEffect(() => {
    if (desktopSettings && !callAudioTouched.current) setIncludeCallAudio(desktopSettings.callAudio);
  }, [desktopSettings]);

  const usersQuery = useQuery({
    queryKey: ["company", "users", "boardroom-roster"],
    queryFn: async () => {
      const raw = await apiGet<{ users?: CompanyUserRow[] }>(companyEndpoints.users);
      return (raw.users ?? []).filter((u) => u.status !== "suspended");
    },
  });

  const roster = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);

  const selfEmail = (user?.email ?? "").toLowerCase();

  // Calendar attendees who are colleagues (same email) become those
  // colleagues — account, photo, shared — and the host's own address is
  // dropped (withHost adds them). Only the `cal_` rows the calendar put there
  // are touched, once the roster has answered. (06-10-2026)
  useEffect(() => {
    if (!fromCalendar?.attendees.length || usersQuery.isLoading) return;
    const byEmail = new Map(roster.map((u) => [(u.email || "").toLowerCase(), u] as const));
    setSelected((prev) => {
      const out: BoardroomDraftParticipant[] = [];
      for (const p of prev) {
        const email = p.email.toLowerCase();
        const fromCal = p.id === `cal_${email}`;
        if (fromCal && selfEmail && email === selfEmail) continue;
        const u = fromCal ? byEmail.get(email) : undefined;
        out.push(
          u
            ? {
                id: u.id,
                name: displayName(u),
                email: u.email,
                isExternal: false,
                shared: true,
                profileImageUrl: u.profile_image_url ?? undefined,
              }
            : p,
        );
      }
      return out;
    });
  }, [fromCalendar, usersQuery.isLoading, roster, selfEmail]);

  const teamRoster = useMemo(
    () => roster.filter((u) => (u.email || "").toLowerCase() !== selfEmail),
    [roster, selfEmail],
  );

  // The account lookup for a typed email (shared with the meeting page's
  // speaker picker). Share starts off for every new search, and stays off
  // for someone already on your roster or with no account.
  const guestLookup = useGuestLookup(pickerSearch);
  useEffect(() => {
    setShareWithGuest(false);
  }, [pickerSearch]);
  useEffect(() => {
    if (guestLookup.status === "inlist" || guestLookup.status === "notfound") {
      setShareWithGuest(false);
    }
  }, [guestLookup.status]);

  const isSelected = (email: string) =>
    selected.some((p) => p.email.toLowerCase() === email.toLowerCase());

  const toggleTeamMember = (u: CompanyUserRow) => {
    const email = u.email;
    if (isSelected(email)) {
      setSelected((prev) => prev.filter((p) => p.email.toLowerCase() !== email.toLowerCase()));
      return;
    }
    setSelected((prev) => [
      ...prev,
      {
        id: u.id,
        name: displayName(u),
        email,
        isExternal: false,
        shared: true,
        profileImageUrl: u.profile_image_url ?? undefined,
      },
    ]);
  };

  const setShared = (email: string, shared: boolean) => {
    setSelected((prev) =>
      prev.map((p) => (p.email.toLowerCase() === email.toLowerCase() ? { ...p, shared } : p)),
    );
  };

  const removeParticipant = (email: string) => {
    setSelected((prev) => prev.filter((p) => p.email.toLowerCase() !== email.toLowerCase()));
  };

  /** Adds from the picker's search (see ExternalFromSearch). */
  const addExternal = ({ name, email }: { name: string; email: string }) => {
    // B48 people rule: a new participant has a name AND an email.
    if (!email.trim() || isSelected(email)) return;
    const registered = guestLookup.status === "found";
    setSelected((prev) => [
      ...prev,
      {
        id: registered
          ? `ext_${email.toLowerCase()}`
          : `guest_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        name,
        email: email.trim(),
        isExternal: true,
        shared: registered && canShareOutside ? shareWithGuest : false,
        isRegisteredUser: registered,
        profileImageUrl:
          registered && guestLookup.status === "found"
            ? (guestLookup.profileImageUrl ?? undefined)
            : undefined,
        nativeLanguage:
          registered && guestLookup.status === "found" ? guestLookup.nativeLanguage : undefined,
      },
    ]);
    setPickerSearch("");
    setGuestName("");
    setShareWithGuest(false);
    // Clearing the search sets the lookup back to idle.
  };

  /** The roster the recording is saved with: the host first, as mobile's
   *  MeetingSetup does (DES 02-10-2026: "start to uskay login say hoi
   *  hai"). Without it a web-started quick meeting had no participants, so
   *  the host's enrolled voice was never matched and the speaker picker did
   *  not know who started it. */
  const withHost = (
    people: BoardroomDraftParticipant[],
  ): BoardroomDraftParticipant[] => {
    if (!user) return people;
    const email = (user.email || "").toLowerCase();
    const rest = people.filter((p) => p.email.toLowerCase() !== email);
    return [
      {
        id: user.id,
        name: user.name || user.email,
        email: user.email,
        isExternal: false,
        shared: true,
        profileImageUrl: user.profileImageUrl ?? undefined,
      },
      ...rest,
    ];
  };

  const agendaTitles = prepNotes.map((n) => n.title?.trim() || "Untitled agenda");

  // "Write new agenda" (DES 12-10-2026, the phone's fromPicker): keep the
  // form in this page's history entry, then open the editor in agenda mode.
  // Saving there comes back here and attaches the agenda (effect above).
  const writeNewAgenda = () => {
    setPrepPickerOpen(false);
    const setupDraft: SetupDraft = {
      tier,
      title,
      prepNotes,
      targetLanguages,
      selected,
      deviceId,
      includeCallAudio: callAudioTouched.current ? includeCallAudio : null,
    };
    const here = (location.state as Record<string, unknown> | null) ?? {};
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...here, setupDraft },
    });
    const editorState: AgendaPickerState = { agendaPicker: true };
    navigate(`${base}/notes/new?kind=agenda`, { state: editorState });
  };

  const onStart = async () => {
    if (notesStartBlocked) return;
    // Optional, as on mobile: an empty title is named by the recorder.
    const trimmed = title.trim();
    if (targetLanguages.length === 0) {
      toast({
        variant: "destructive",
        title: "Language required",
        description: "Pick at least one target output language.",
      });
      return;
    }
    const supportErr = describeRecorderSupportError();
    if (supportErr) {
      toast({
        variant: "destructive",
        title: "Recording not supported",
        description: supportErr,
      });
      return;
    }
    if (micError) {
      toast({
        variant: "destructive",
        title: "Microphone unavailable",
        description: micError,
      });
      return;
    }
    setStarting(true);
    try {
      const durability = await probeDurableStorage();
      if (!durability.ok) {
        toast({
          variant: "destructive",
          title: "Draft storage unavailable",
          description: durability.error,
        });
        return;
      }
      await refresh();
      // The Notes page starts its next Notes meeting in this language.
      if (isNotes) saveNotesLanguage(targetLanguages[0]);
      navigate(`${base}/meetings/record`, {
        state: {
          title: trimmed,
          // Notes: one language only, even if Full had several picked first.
          targetLanguages: isNotes ? targetLanguages.slice(0, 1) : targetLanguages,
          // Sharing v2 (DES 21-10-2026): Notes shares exactly like a
          // Boardroom, from the same per-person Share switch. The owner's
          // typed points stay private on the server either way.
          participants: withHost(selected),
          deviceId: deviceId || undefined,
          includeSystemAudio: desktopCallAudio && includeCallAudio,
          // Meeting Notes has no agenda check (ADR-030), so no agenda rides along.
          prepNoteIds: isNotes ? [] : prepNotes.map((n) => n.id),
          tier,
          // GDPR (06-10-2026): the slide carries the confirmation shown above it.
          recordingNotice: { method: "setup_slide", at: new Date().toISOString() },
        },
      });
    } finally {
      setStarting(false);
    }
  };

  const externals = selected.filter((p) => p.isExternal);

  // Mobile's summary: the host is always in, counted as "You".
  const participantsSummary = (() => {
    const team = selected.length - externals.length;
    const parts = ["You"];
    if (team > 0) parts.push(`${team} company user${team === 1 ? "" : "s"}`);
    if (externals.length > 0) parts.push(`${externals.length} external`);
    return parts.join(", ");
  })();

  const stackPeople = useMemo(() => {
    const host = user
      ? {
          key: `host-${user.id}`,
          name: user.name || user.email,
          email: user.email,
          profileImageUrl: user.profileImageUrl ?? undefined,
          isHost: true as const,
          isExternal: false,
          shared: true,
        }
      : null;
    const rest = selected.map((p) => ({
      key: p.email,
      name: p.name,
      email: p.email,
      profileImageUrl: p.profileImageUrl,
      isHost: false as const,
      isExternal: p.isExternal,
      shared: p.shared !== false,
      isRegisteredUser: p.isRegisteredUser,
    }));
    return host ? [host, ...rest] : rest;
  }, [selected, user]);

  // External people added so far come first (ticked; click to remove), then
  // the colleagues matching the search. The search is controlled here so the
  // same text can drive ExternalFromSearch.
  const pickerItems: RosterPickerItem[] = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const matches = (keys: string[]) => !q || keys.some((k) => k.toLowerCase().includes(q));
    const ext: RosterPickerItem[] = externals
      .filter((p) => matches([p.name, p.email]))
      .map((p) => ({
        id: p.id ?? p.email,
        label: p.name,
        subtitle: isPlaceholderEmail(p.email) ? "External participant" : `External · ${p.email}`,
        external: p,
      }));
    const team: RosterPickerItem[] = teamRoster
      .filter((u) => matches([displayName(u), u.email, u.first_name ?? "", u.last_name ?? ""]))
      .map((u) => ({
        id: u.id,
        label: displayName(u),
        subtitle: u.email,
        user: u,
      }));
    return [...ext, ...team];
  }, [externals, teamRoster, pickerSearch]);

  const selectedPickerIds = useMemo(
    () =>
      selected
        .map((p) => (p.isExternal ? (p.id ?? p.email) : p.id))
        .filter((id): id is string => Boolean(id)),
    [selected],
  );

  const searchEmail = pickerSearch.trim();
  const externalBlock = hasExternalBlock(pickerSearch) ? (
    <ExternalFromSearch
      query={pickerSearch}
      lookup={guestLookup}
      guestName={guestName}
      setGuestName={setGuestName}
      share={canShareOutside ? shareWithGuest : undefined}
      setShare={canShareOutside ? setShareWithGuest : undefined}
      blockedMessage={
        isFullEmail(searchEmail) && isSelected(searchEmail) ? "Already a participant." : null
      }
      requireEmail
      takenEmails={selected.map((p) => p.email)}
      onAdd={addExternal}
    />
  ) : null;

  return (
    // Two columns on desktop (30-09-2026, DES: "meeting setup hamesha poora
    // nazar aaye"): heading, mic and call audio on the left, the form and the
    // slide on the right, so the whole form fits without scrolling. Narrow
    // screens stack as before.
    // 01-10-2026, DES: the Interpreter's form style ("boardroom setup ki bhi
    // same form style say match karoo"), without its avatars. Two rows: the
    // title on its own, then Microphone and the form side by side so their
    // first fields line up.
    // 03-10-2026, DES: no title, as the Interpreter. Left: the back link
    // (named after the page it opens), Microphone straight under it. Right:
    // the form from the top.
    <div className="mx-auto max-w-6xl px-4 pb-16 pt-6 md:px-5 lg:grid lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] lg:gap-x-10 lg:gap-y-6 lg:pb-6 lg:pt-10">
      <aside className="max-w-2xl lg:row-span-2 lg:flex lg:max-w-none lg:flex-col">
        <div>
          {/* Back to the meetings list; replaces the Cancel button that sat
              alone at the column's far edge (30-09-2026). */}
          <Link
            to={`${base}/meetings`}
            className="-ml-1 inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Boardrooms
          </Link>
        </div>

        {/* Divider on the right in the field-hairline colour, centred in the
            40px column gap and as tall as the form; the inner box stays
            sticky. Same as the Interpreter. */}
        <div className="mt-6 lg:-mr-5 lg:flex-1 lg:border-r lg:border-app-text/10 lg:pr-5">
          <div className="space-y-3 lg:sticky lg:top-3">
            <FormFieldGroup
              label="Microphone"
              hint={describeMicInUse(devices, deviceId, micsLoading)}
            >
              <MicPicker
                compact
                devices={devices}
                value={deviceId}
                onChange={setDeviceId}
                loading={micsLoading}
                error={null}
              />
            </FormFieldGroup>

            {desktopCallAudio ? (
              <label className="flex items-center justify-between gap-3 border-b border-app-text/10 pb-2.5">
                <span className="min-w-0">
                  <span className="block text-[15px] font-medium">Include call audio</span>
                  <span className="block text-xs text-muted-foreground">
                    Records the other side of a Zoom, Teams or Meet call, even with headphones on.
                  </span>
                </span>
                <Switch
                  checked={includeCallAudio}
                  onCheckedChange={(v) => {
                    callAudioTouched.current = true;
                    setIncludeCallAudio(v);
                  }}
                  aria-label="Include call audio"
                />
              </label>
            ) : null}

            {micError ? (
              <p className="text-sm text-destructive" role="alert">
                {micError}
              </p>
            ) : null}

            {/* Not registered yet: Meeting Intel cannot tell the host's lines
                from anyone else's (04-10-2026). Quiet, one line, never blocks. */}
            {voice.data && voice.data.status !== "enrolled" ? (
              <p className="text-[13px] leading-[18px] text-muted-foreground">
                <Link
                  to={`${base}/account/voice`}
                  className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                >
                  <AudioLines className="h-3.5 w-3.5" aria-hidden />
                  Register your voice
                </Link>{" "}
                so Meeting Intel knows which lines are yours.
              </p>
            ) : null}

            <p className="hidden text-[13px] leading-[18px] text-muted-foreground opacity-70 lg:block">
              Keep this tab open while recording. Drafts survive refresh.
            </p>
          </div>
        </div>
      </aside>

      <div
        className={cn(
          // Up with the left column (DES 03-10-2026, "right bhi iskay
          // according ooper move karo"): its first field level with
          // Microphone (lg:mt-10 = the back link's row + its gap).
          "mt-6 max-w-2xl space-y-5 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-10",
          formGridLinesHiddenClass,
        )}
      >
        {/* Mode first (DES 07-10-2026): it decides which fields below
            apply. Then the title on its own row, Participants and Languages
            side by side, Agenda last on its own row (DES 01-10-2026). */}
        <FormSheetFields>
          {/* Mode (ADR-030). */}
          <FormSheetField span={2}>
            <FormFieldGroup label="Mode" hint={MODE_HINT[tier]}>
              <div className="pt-1">
                <FormSheetTabs<BoardroomMeetingTier>
                  compact
                  label="Meeting mode"
                  tabs={MODE_TABS}
                  value={tier}
                  onChange={changeTier}
                />
              </div>
              {!isNotes && intelligenceNotice ? (
                <p className="mt-2 text-[13px] leading-[18px] text-muted-foreground">
                  {intelligenceNotice}
                </p>
              ) : null}
              {/* Free minutes (B4) / plan fair use (B6): Meeting Notes only. */}
              {isNotes ? <FreeMinutesNotice className="mt-2" /> : null}
            </FormFieldGroup>
          </FormSheetField>

          <FormSheetField span={2}>
            <FormFieldGroup
              label="Meeting title (optional)"
              htmlFor="title"
              hint="Leave it blank and we'll name it for you."
            >
              <Input
                capitalizeFirst
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Meeting title (optional)"
                autoFocus
                maxLength={100}
              />
            </FormFieldGroup>
          </FormSheetField>

          {/* Participants: a 56px avatar stack (the flag stack's size beside
              it) over the picker row. Meeting Notes shows it again (DES
              10-10-2026, hidden since 06-10), with the same Share switches
              as a Boardroom since 21-10-2026 (sharing v2). */}
          <FormSheetField>
            <FormFieldGroup
              label={
                stackPeople.length > 0 ? `Participants (${stackPeople.length})` : "Participants"
              }
            >
              <div className="space-y-3">
                {stackPeople.length > 0 ? (
                  !participantsExpanded ? (
                    <button
                      type="button"
                      onClick={() => setParticipantsExpanded(true)}
                      className="flex items-center gap-3 py-1 text-left"
                      aria-label="Show participants"
                    >
                      <span className="flex items-center">
                        {stackPeople.slice(0, STACK_MAX).map((p, index) => (
                          <span
                            key={p.key}
                            className="relative inline-flex rounded-full ring-2 ring-background"
                            style={{
                              marginLeft: index === 0 ? 0 : -STACK_OVERLAP,
                              zIndex: stackPeople.length - index,
                            }}
                          >
                            <Avatar
                              src={p.profileImageUrl}
                              alt={p.name}
                              initials={getInitials(p.name)}
                              size="xl"
                            />
                          </span>
                        ))}
                        {stackPeople.length > STACK_MAX ? (
                          <span
                            className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-foreground ring-2 ring-background"
                            style={{
                              marginLeft: -STACK_OVERLAP,
                              zIndex: stackPeople.length + 1,
                            }}
                            aria-label={`${stackPeople.length - STACK_MAX} more`}
                          >
                            +{stackPeople.length - STACK_MAX}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {stackPeople.map((p) => (
                          <div
                            key={p.key}
                            className="flex min-w-[11rem] shrink-0 items-center gap-2 rounded-full bg-secondary/70 py-1.5 pl-1.5 pr-2"
                          >
                            <Avatar
                              src={p.profileImageUrl}
                              alt={p.name}
                              initials={getInitials(p.name)}
                              size="sm"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-semibold">
                                {p.isHost ? "You" : p.name.split(" ")[0]}
                                {p.isExternal ? (
                                  <span className="ml-1 text-[9px] font-semibold uppercase text-amber-700">
                                    Ext
                                  </span>
                                ) : null}
                              </p>
                            </div>
                            {!p.isHost && canShareWith(p) ? (
                              <Switch
                                checked={p.shared}
                                onCheckedChange={(v) => setShared(p.email, v)}
                                aria-label={`Share with ${p.name}`}
                              />
                            ) : null}
                            {!p.isHost ? (
                              <button
                                type="button"
                                className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                aria-label={`Remove ${p.name}`}
                                onClick={() => removeParticipant(p.email)}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
                        onClick={() => setParticipantsExpanded(false)}
                      >
                        Collapse
                      </button>
                    </div>
                  )
                ) : null}

                {/* One picker for colleagues and external people (mobile parity) */}
                <PickerSheet<RosterPickerItem>
                  anchor={
                    <button type="button" disabled={usersQuery.isLoading} className={fieldRowClass}>
                      <Users className={fieldRowIconClass} aria-hidden />
                      <span
                        className={cn(
                          fieldRowTextClass,
                          usersQuery.isLoading && "text-muted-foreground",
                        )}
                      >
                        {usersQuery.isLoading ? "Loading roster…" : participantsSummary}
                      </span>
                      <ChevronDown className={fieldRowChevronClass} aria-hidden />
                    </button>
                  }
                  open={rosterOpen}
                  onOpenChange={(open) => {
                    setRosterOpen(open);
                    if (!open) {
                      setPickerSearch("");
                      setGuestName("");
                    }
                  }}
                  title="Participants"
                  subtitle="Search colleagues, or type a name or email to add someone from outside."
                  searchPlaceholder="Name or email…"
                  searchValue={pickerSearch}
                  onSearchChange={setPickerSearch}
                  items={pickerItems}
                  multiSelect
                  selectedIds={selectedPickerIds}
                  onSelect={(item) =>
                    item.user ? toggleTeamMember(item.user) : removeParticipant(item.external.email)
                  }
                  clearable={false}
                  loading={usersQuery.isLoading}
                  listHeader={externalBlock}
                  renderLeftAccessory={(item) =>
                    item.user ? (
                      <Avatar
                        src={item.user.profile_image_url}
                        alt={item.label}
                        initials={getInitials(item.label)}
                        size="xl"
                      />
                    ) : item.external.profileImageUrl ? (
                      <Avatar
                        src={item.external.profileImageUrl}
                        alt={item.label}
                        initials={getInitials(item.label)}
                        size="xl"
                      />
                    ) : (
                      <span className="flex items-center justify-center rounded-full bg-secondary">
                        <UserRound className="h-6 w-6 text-muted-foreground" />
                      </span>
                    )
                  }
                  renderTrailingAccessory={(item, isOn) => {
                    if (!isOn) return null;
                    const email = item.user ? item.user.email : item.external.email;
                    const part = selected.find(
                      (p) => p.email.toLowerCase() === email.toLowerCase(),
                    );
                    // Guests have no account to share with. Notes shares like
                    // a Boardroom since 21-10-2026 (sharing v2).
                    if (!part || !canShareWith(part)) return null;
                    return (
                      <label
                        className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Share
                        <Switch
                          checked={part.shared !== false}
                          onCheckedChange={(v) => setShared(email, v)}
                          aria-label={`Share with ${item.label}`}
                        />
                      </label>
                    );
                  }}
                  emptyText="No company users found."
                />
              </div>
            </FormFieldGroup>
          </FormSheetField>

          {/* Target Output Languages: a stack over a picker row, like
              Participants. Meeting Notes: one "Notes Language", picked from
              a single-select menu that shows its name (the phone's 0e90939);
              the whole row, as Participants is gone. */}
          {isNotes ? (
            <FormSheetField span={2}>
              <FormFieldGroup label="Notes Language">
                <LanguagePicker
                  value={targetLanguages[0] ?? null}
                  // A pick replaces it; clearing keeps the one there is.
                  onChange={(id) => {
                    if (id) setTargetLanguages([id]);
                  }}
                  placeholder="Pick a language"
                  title="Notes Language"
                  subtitle="Your summary and to-dos are written in this language."
                  variant="field"
                  triggerClassName="min-h-6 py-0 leading-6"
                />
              </FormFieldGroup>
            </FormSheetField>
          ) : (
          <FormSheetField>
            <FormFieldGroup
              label={
                targetLanguages.length > 0
                  ? `Target output languages (${targetLanguages.length})`
                  : "Target output languages"
              }
            >
              <LanguagePicker
                multiSelect
                values={targetLanguages}
                onValuesChange={setTargetLanguages}
                placeholder="Pick target languages"
                variant="field"
                triggerClassName="min-h-6 py-0 leading-6"
              />
            </FormFieldGroup>
          </FormSheetField>
          )}

          {/* Agenda — optional, and said so: a meeting without one is the
              normal case, and a field that looks required would imply
              otherwise. Last on the form (DES 01-10-2026). Not for Meeting
              Notes, which does no agenda check: an agenda there would be
              silently ignored (DES 06-10-2026). */}
          {isNotes ? null : (
          <FormSheetField span={2}>
            <FormFieldGroup
              label="Agenda (optional)"
              hint={`Up to ${MAX_AGENDA_NOTES} agendas from your Agendas.`}
            >
              {/* One line whatever the count (DES 02-10-2026: "notes add kartay
                  hain to yeh list lambi hoti jati hai aur page apni jaga say
                  khiakta rehta hai"): the row names the notes, the menu adds
                  and removes them (untick). Was a row per note with an ×. */}
              <div>
                {/* Multi-select (DES 01-10-2026): tick several notes in one
                    go; the menu stays open and stops at MAX_AGENDA_NOTES. */}
                <NotePickerSheet
                  anchor={
                    <button
                      type="button"
                      className={fieldRowClass}
                      aria-label="Choose agendas"
                    >
                      {prepNotes.length > 0 ? (
                        <NotebookPen className={cn(fieldRowIconClass, "text-brand")} aria-hidden />
                      ) : (
                        <ListOrdered className={fieldRowIconClass} aria-hidden />
                      )}
                      <span
                        className={cn(
                          fieldRowTextClass,
                          prepNotes.length === 0 && "text-muted-foreground",
                        )}
                        title={agendaTitles.join("\n") || undefined}
                      >
                        {prepNotes.length === 0
                          ? "Pick agendas"
                          : prepNotes.length === 1
                            ? agendaTitles[0]
                            : `${prepNotes.length} agendas · ${agendaTitles.join(", ")}`}
                      </span>
                      <ChevronDown className={fieldRowChevronClass} aria-hidden />
                    </button>
                  }
                  open={prepPickerOpen}
                  onOpenChange={setPrepPickerOpen}
                  selectedId={null}
                  onPick={() => {}}
                  multiple
                  max={MAX_AGENDA_NOTES}
                  // Setup only: during a recording the agenda is read only.
                  onWriteNew={writeNewAgenda}
                  selectedIds={prepNotes.map((n) => n.id)}
                  onToggle={(picked) =>
                    setPrepNotes((cur) =>
                      cur.some((n) => n.id === picked.id)
                        ? cur.filter((n) => n.id !== picked.id)
                        : cur.length >= MAX_AGENDA_NOTES
                          ? cur
                          : [...cur, picked],
                    )
                  }
                />
              </div>
            </FormFieldGroup>
          </FormSheetField>
          )}
        </FormSheetFields>
        {/* Under the fields, smaller and dimmer, as on mobile; inset to the
            field hairlines. */}
        <p className="pl-4 pr-[17px] text-[13px] leading-[18px] text-muted-foreground opacity-70 md:pl-5 md:pr-[21px]">
          {isNotes
            ? "Your summary and to-dos are written in this language. The transcript stays in the language spoken."
            : "Boardroom output will be translated and synthesized into the languages you pick."}
        </p>
        {isNotes ? (
          <p className="pl-4 pr-[17px] text-[13px] leading-[18px] text-muted-foreground opacity-70 md:pl-5 md:pr-[21px]">
            Points you type while recording stay private to you.
          </p>
        ) : null}

        {/* Exactly as wide as the field hairlines above: the cells' px-4 /
            md:px-5 inset, plus the 1px transparent border-r on the right. */}
        {/* GDPR Art 13 (06-10-2026): the slide is the confirmation. */}
        <p className="pl-4 pr-[17px] text-[13px] leading-[18px] text-muted-foreground md:pl-5 md:pr-[21px]">
          By sliding to start, you confirm everyone here knows this meeting is recorded and transcribed.
        </p>
        {/* ADR-051: said once someone outside the company is picked. */}
        {shareLine && selected.some((p) => p.isExternal && p.isRegisteredUser) ? (
          <p className="pl-4 pr-[17px] text-[13px] leading-[18px] text-muted-foreground md:pl-5 md:pr-[21px]">
            {shareLine} People outside it can be named as speakers but get no copy.
          </p>
        ) : null}
        {/* Sharing v2 (DES 21-10-2026, decision 5): company members only. */}
        {adminsLine ? (
          <p className="pl-4 pr-[17px] text-[13px] leading-[18px] text-muted-foreground md:pl-5 md:pr-[21px]">
            {adminsLine}
          </p>
        ) : null}
        <div className="mt-2 pl-4 pr-[17px] md:pl-5 md:pr-[21px] lg:mt-1">
          <SwipeButton
            // Says what it starts (DES 21-10-2026): Notes mode is not a Boardroom.
            text={isNotes ? "Slide to start notes" : "Slide to Start Boardroom"}
            icon={Mic}
            loading={starting}
            disabled={starting || notesStartBlocked}
            onSwipeComplete={() => void onStart()}
            accessibilityLabel={isNotes ? "Start meeting notes" : "Start boardroom"}
            accessibilityHint={
              isNotes
                ? "Slide or press Enter to start recording meeting notes"
                : "Slide or press Enter to start a live boardroom recording"
            }
          />
        </div>
        <button
          type="button"
          className="mx-auto flex items-center justify-center gap-1.5 text-sm font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          onClick={() => setScheduleOpen(true)}
        >
          <CalendarClock className="h-4 w-4" aria-hidden />
          Schedule for later
        </button>
        <p className="text-center text-xs text-muted-foreground sm:text-left lg:hidden">
          <Plus className="mr-1 inline h-3 w-3" />
          Keep this tab open while recording. Drafts survive refresh.
        </p>
      </div>

      <ScheduleBoardroomDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        title={title}
        targetLanguages={targetLanguages}
        participants={selected}
        prepNoteIds={scheduleAgenda(prepNotes.map((n) => n.id), isNotes).prepNoteIds}
        onScheduled={() => {
          navigate(`${base}/meetings/scheduled`);
        }}
        // New meeting (DES 10-10-2026, scheduleFlow.ts, same as the phone):
        // clear this form and REPLACE setup with Scheduled Meetings, so Back
        // returns to where setup was opened from, never to a filled form.
        onResetForm={() => {
          setTitle("");
          setSelected([]);
          setTargetLanguages(["en-GB"]);
          setPrepNotes([]);
          setTier("full");
        }}
        onCreated={({ highlightId }) => {
          navigate(`${base}/meetings/scheduled`, { replace: true, state: { highlightId } });
        }}
      />
    </div>
  );
}
